import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { workAuthorService } from "../services/workAuthorService";
import { workAuthorDisplayId } from "./WorkAuthorCommonStore";
import { WorkAuthor } from "../../../api/graphql";
import { authorComposerDisplayId } from "../../authorComposer/store/AuthorComposerCommonStore";
import { getWorkAuthorPublisherValues } from "../../workAuthorPublisher/store/WorkAuthorPublisherCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { CrudRows } from "../../common/types";
import { authorComposerService } from "../../authorComposer/services/authorComposerService";
import { InitializeParams } from "../../../store/types";

export class WorkAuthorViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = workAuthorService;
  workAuthorInstance: WorkAuthor | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  authorService = authorComposerService;
  authorSearchValue: string = "";
  authorIdValue: string = "";
  authorSelectedValue: { displayId: string; id: string } | null = null;
  lyricsPercentageValue: number = 0;
  musicPercentageValue: number = 0;
  publishersColumnWidths: Record<string, number | string> = {};
  publishersRows: CrudRows = {};

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.publishersColumnWidths = {
      __publisher: 200,
      percentage: 200,
      durationInYears: 200,
      autoRenewalYears: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadWorkAuthorById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.workAuthorOne({ id });
    this.workAuthorInstance = result.workAuthorOne;
    if (this.workAuthorInstance) {
      this.loadValues(this.workAuthorInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.workAuthorInstance) {
      return workAuthorDisplayId(this.workAuthorInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.WorkAuthorSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  viewFormAuthor() {
    const id = this.workAuthorInstance?.author?.id;
    if (id) {
      this.rootStore.openOrPushOverlay("authorComposer", id, "view");
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "author") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.authorComposerOne({
          id: params.returnData.resultId,
        });
        const authorComposer = result.authorComposerOne;
        this.authorSelectedValue = authorComposer;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get authorValue() {
    if (this.authorSelectedValue) {
      return {
        id: this.authorSelectedValue.id,
        displayId: authorComposerDisplayId(
          this.authorSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get publishersVisibleColumns() {
    return [
      {
        name: "__publisher",
        title: this.langStore.WorkAuthorPublisherPublisherLabel,
        width: this.publishersColumnWidths["__publisher"],
      },
      {
        name: "percentage",
        title: this.langStore.WorkAuthorPublisherPercentageLabel,
        width: this.publishersColumnWidths["percentage"],
      },
      {
        name: "durationInYears",
        title: this.langStore.WorkAuthorPublisherDurationInYearsLabel,
        width: this.publishersColumnWidths["durationInYears"],
      },
      {
        name: "autoRenewalYears",
        title: this.langStore.WorkAuthorPublisherAutoRenewalYearsLabel,
        width: this.publishersColumnWidths["autoRenewalYears"],
      },
    ];
  }

  publishersSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.publishersColumnWidths = values;
  }

  get publishersRowsDyn() {
    const entries = Object.entries(this.publishersRows).map(([key, value]) => [
      key,
      {
        ...value,
        __publisher: this.langStore.displayIdByLang(value.__publisher),
      },
    ]);
    return Object.fromEntries(entries);
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.workAuthorInstance = null;
    this.authorSearchValue = "";
    this.authorIdValue = "";
    this.lyricsPercentageValue = 0;
    this.musicPercentageValue = 0;
    this.publishersRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelWorkAuthor() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editWorkAuthor() {
    const id = this.workAuthorInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/workAuthor/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printWorkAuthor(handlePrint: () => void): void {
    handlePrint();
  }

  *closeWorkAuthor(): any {
    yield this.close();
  }

  loadValues(instance: WorkAuthor) {
    this.idValue = instance.id;
    this.authorSelectedValue = instance.author;
    this.lyricsPercentageValue = instance.lyricsPercentage;
    this.musicPercentageValue = instance.musicPercentage;
    this.publishersRows = instance.publishers
      ? Object.assign(
          {},
          ...instance.publishers.map((x) => {
            const processedRow = getWorkAuthorPublisherValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadWorkAuthorById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
